import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';

import { ApiError } from '@/lib/api/errors';
import type { ValidationDetail } from '@/lib/validation/server-details';

import { AUTH_ERROR_CODES } from './constants';

/**
 * Puts a `VALIDATION_FAILED` 400's `details[]` onto the form's own fields, so a reason that names a
 * field is shown inline under it rather than in a toast (ADO 121341: a non-US phone refused at
 * login). Returns `true` when every detail landed on a field — the caller then skips its toast. A
 * detail naming a field the form does not have leaves the whole error to the toast, which shows the
 * detail text (`authErrorMessage`).
 */
export function applyServerFieldErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fields: readonly Path<T>[],
): boolean {
  if (!(error instanceof ApiError) || error.code !== AUTH_ERROR_CODES.VALIDATION_FAILED) {
    return false;
  }
  const details = (Array.isArray(error.details) ? error.details : []) as ValidationDetail[];
  const known = new Set<string>(fields);
  if (!details.length || !details.every((detail) => known.has(detail.field))) return false;
  for (const detail of details) {
    setError(detail.field as Path<T>, { type: 'server', message: detail.message });
  }
  return true;
}
